import React, { Dispatch, SetStateAction } from 'react';
import {
    PaginationType,
    emptyPagination,
    mapPCPs,
    useLocalStorageState,
    useServiceSubscribe,
} from '~/util';
import { ServicesContext, AuthContext, AuthContextType, ClientContext } from '~/services';

// filter types
export interface PCPType {
    value: number;
    label: string;
    clinicName: string;
    duplicate?: boolean;
}
export interface ClinicType {
    value: number;
    label: string;
    pcps: PCPType[];
}
interface RiskBandType {
    value: number;
    label: string;
}
export type PCPList = PCPType[] | null;
export type ClinicList = ClinicType[] | null;
export type RiskBandList = RiskBandType[] | null;
interface SearchInputObject {
    value: null | string | boolean;
    label: string;
}
interface StoredSearchContext {
    clinic: ClinicList;
    PCP: PCPList;
}
export interface GapOptionType {
    id: number;
    name: string;
    description: string;
    type: number;
    label: string;
    value: string;
}
export interface SearchFilterType {
    label: string;
    firstName: string;
    lastName: string;
    DOB: null | string | Date;
    memberID: string;
    phone: string;
    GMPI: string;
    planType: SearchInputObject;
    riskBand: RiskBandList;
    openGaps: null | GapOptionType[];
    clinic: ClinicList;
    PCP: PCPList;
    insurance: SearchInputObject[];
    disenrolled: SearchInputObject;
    riskFactor: SearchInputObject;
}
export interface SearchValuesType extends Omit<SearchFilterType, 'label'> {
    filter: null | SearchFilterType;
}

// column preset types
export type SearchColumn = keyof typeof colFieldMap;
export interface ColumnPresetType {
    label: string;
    columns: SearchColumn[];
}

interface SearchSort {
    field: string;
    dir: string;
}

export interface SearchContextType {
    clinicList: ClinicList;
    pcpList: PCPList;
    insurancesList: SearchInputObject[];
    stored: StoredSearchContext;
    setStored: Dispatch<SetStateAction<StoredSearchContext>>;
    searchValues: SearchValuesType;
    setSearchValues: Dispatch<SetStateAction<SearchValuesType>>;
    paginationRef: React.MutableRefObject<PaginationType>;
    resetPagination: () => void;
    sort: SearchSort;
    setSort: Dispatch<SetStateAction<SearchSort>>;
    selectedPreset: string;
    setSelectedPreset: Dispatch<SetStateAction<string>>;
    savedColumnPresets: ColumnPresetType[];
    setSavedColumnPresets: Dispatch<SetStateAction<ColumnPresetType[]>>;
    customColumns: SearchColumn[] | null;
    setCustomColumns: Dispatch<SetStateAction<SearchColumn[]>>;
    columnPresets: ColumnPresetType[];
}

const SearchContext = React.createContext<SearchContextType | null>(null);

export const initialSearch: SearchValuesType = {
    filter: null,
    firstName: '',
    lastName: '',
    DOB: null,
    memberID: '',
    GMPI: '',
    phone: '',
    planType: { value: null, label: 'All' },
    riskBand: [],
    openGaps: [],
    clinic: [],
    PCP: [],
    insurance: [],
    disenrolled: { value: null, label: 'Currently Assigned' },
    riskFactor: { value: null, label: 'All' },
};

export const defaultColumnPresets: ColumnPresetType[] = [
    {
        label: 'Pending',
        columns: ['ICD Pending', 'AW Pending', 'Qua Pending', 'Risk Band', 'Last OV'],
    },
    {
        label: 'In Progress',
        columns: ['ICD Saved On', 'AW Saved On', 'Qua Saved On'],
    },
    {
        label: 'HCC/ICD',
        columns: ['ICD Pending', 'ICD Failed', 'ICD Review', 'ICD Addr'],
    },
    {
        label: 'AW',
        columns: ['AW Pending', 'AW Review', 'AW Addr'],
    },
    {
        label: 'Quality',
        columns: ['Qua Pending', 'Qua Failed', 'Qua Review', 'Qua Addr'],
    },
];

export const colFieldMap = {
    'Risk Band': 'patient_record.risk_band_level',
    'Last OV': 'patient_record.last_office_visit',
    'ICD Saved On': 'gap_totals.risk_gaps.oldest_answer_at',
    'ICD Pending': 'gap_totals.risk_gaps.pending',
    'ICD Failed': 'gap_totals.risk_gaps.failed',
    'ICD Review': 'gap_totals.risk_gaps.under_review',
    'ICD Addr': 'gap_totals.risk_gaps.addressed',
    'AW Saved On': 'gap_totals.foc_gaps.oldest_answer_at',
    'AW Pending': 'gap_totals.foc_gaps.pending',
    'AW Review': 'gap_totals.foc_gaps.under_review',
    'AW Addr': 'gap_totals.foc_gaps.addressed',
    'Qua Saved On': 'gap_totals.quality_gaps.oldest_answer_at',
    'Qua Pending': 'gap_totals.quality_gaps.pending',
    'Qua Failed': 'gap_totals.quality_gaps.failed',
    'Qua Review': 'gap_totals.quality_gaps.under_review',
    'Qua Addr': 'gap_totals.quality_gaps.addressed',
};

function SearchProvider(props: { children: JSX.Element }) {
    const context = React.useContext(ServicesContext);
    const { authenticated } = React.useContext(AuthContext) as AuthContextType;
    const { reportYear } = React.useContext(ClientContext);
    const initStorage: { clinic: ClinicList; PCP: PCPList } = { clinic: null, PCP: null };
    const { item: stored, setItem: setStored } = useLocalStorageState(
        'patient-search',
        initStorage,
        true
    );
    const [sort, setSort] = React.useState<SearchSort>({ field: 'first_name', dir: 'ASC' });
    const paginationRef = React.useRef<PaginationType>(emptyPagination());
    // filter state
    const [clinicList, setClinicList] = React.useState<ClinicList>([]);
    const [pcpList, setPcpList] = React.useState<PCPList>([]);
    const [insurancesList, setInsurancesList] = React.useState<SearchInputObject[]>([]);
    const [searchValues, setSearchValues] = React.useState<SearchValuesType>({ ...initialSearch });

    // column preset state
    const [savedColumnPresets, setSavedColumnPresets] = React.useState<ColumnPresetType[]>([]);
    const [customColumns, setCustomColumns] =
        React.useState<SearchContextType['customColumns']>(null);
    const [selectedPreset, setSelectedPreset] = React.useState('Pending');
    const columnPresets = [
        ...defaultColumnPresets
            .filter(
                (preset) => !(context.user.hasFeatureToggle('hidden_foc') && preset.label === 'AW')
            )
            .map((preset) => ({
                ...preset,
                columns: preset.columns.filter(
                    (col) =>
                        !(
                            context.user.hasFeatureToggle('hidden_foc') &&
                            col.substring(0, 2) === 'AW'
                        )
                ),
            })),
        ...savedColumnPresets,
        ...(customColumns ? [{ label: 'Custom', columns: customColumns }] : []),
    ] as ColumnPresetType[];

    // load saved presets
    const loadSavedColumnPresets = React.useCallback(() => {
        setSavedColumnPresets(
            JSON.parse(JSON.stringify(context.user.getSavedSearchData().savedColumnPresets))
        );
    }, [context.user]);
    React.useEffect(() => {
        loadSavedColumnPresets();
    }, [loadSavedColumnPresets]);
    useServiceSubscribe(context.user.events.savedSearchDataUpdated, loadSavedColumnPresets);
    useServiceSubscribe(context.user.events.userLoggedIn, loadSavedColumnPresets);

    React.useEffect(() => {
        // Load the clinics list on login or page load
        (async () => {
            if (authenticated) {
                const clinics = await context.api.call({
                    method: 'get',
                    url: `/api/clinics?report_year=${reportYear}`,
                });
                setClinicList(
                    clinics.data.map((x: TODO_TYPEME) => ({
                        value: x.id,
                        label: x.name,
                        pcps: x.pcps.map((y: TODO_TYPEME) => ({
                            value: y.id,
                            label: y.name,
                            clinicName: x.name,
                        })),
                    }))
                );
            }
        })();
        (async () => {
            if (authenticated) {
                const result = await context.api.call({
                    method: 'get',
                    url: '/api/insurances',
                });
                setInsurancesList(
                    result.data['hydra:member'].map((x: TODO_TYPEME) => ({
                        value: x.id,
                        label: x.name,
                    }))
                );
            }
        })();
    }, [context.api, authenticated, reportYear]);

    React.useEffect(() => {
        // Update the PCP list any time the clinic changes
        setPcpList(
            mapPCPs(stored.clinic !== null && stored.clinic.length > 0 ? stored.clinic : clinicList)
        );
    }, [stored.clinic, clinicList]);

    function resetPagination() {
        paginationRef.current = {
            ...emptyPagination(),
            items_per_page: paginationRef.current.items_per_page,
        };
    }

    const methods: SearchContextType = {
        clinicList,
        pcpList,
        insurancesList,
        stored,
        setStored,
        searchValues,
        setSearchValues,
        paginationRef,
        resetPagination,
        sort,
        setSort,
        selectedPreset,
        setSelectedPreset,
        savedColumnPresets,
        setSavedColumnPresets,
        customColumns,
        setCustomColumns,
        columnPresets,
    };
    return <SearchContext.Provider value={methods} {...props} />;
}

export function useSearchClinic() {
    const { stored, setStored } = React.useContext(SearchContext) as SearchContextType;
    const setClinic = React.useCallback(
        (clinic: ClinicList) => {
            setStored((prev) => ({
                clinic,
                PCP:
                    Array.isArray(prev.PCP) && Array.isArray(clinic)
                        ? prev.PCP.filter((x) =>
                              mapPCPs(clinic).some((y: PCPType) => y.value === x.value)
                          )
                        : prev.PCP,
            }));
        },
        [setStored]
    );

    return { clinic: stored.clinic, setClinic };
}

export function useSearchPCP() {
    const { stored, setStored } = React.useContext(SearchContext) as SearchContextType;
    const setPCP = React.useCallback(
        (PCP: PCPList) => {
            setStored((prev) => ({ ...prev, PCP }));
        },
        [setStored]
    );

    return { PCP: stored.PCP, setPCP };
}

export function useSearchSort() {
    const { sort, setSort } = React.useContext(SearchContext) as SearchContextType;
    function getSortDirection(field: string, prev: SearchSort) {
        if (prev.field === field) {
            if (prev.dir === 'ASC') return 'DESC';
            return 'ASC';
        }
        if (field.includes('gap_totals') || field.includes('risk_band_level')) return 'DESC';
        return 'ASC';
    }
    function updateSort(field: string) {
        setSort((prev) => ({
            field,
            dir: getSortDirection(field, prev),
        }));
    }
    function resetSort() {
        setSort({ field: 'first_name', dir: 'ASC' });
    }

    return { sort, updateSort, resetSort };
}

export { SearchContext, SearchProvider };
